import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { MEDIA, SPECTORA_URL } from "@/lib/media";

const LINKS = [
  { label: "Services", target: "services" },
  { label: "Process", target: "process" },
  { label: "Why Us", target: "whyus" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (target) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-2xl bg-ink/70 border-b border-white/10" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img src={MEDIA.logo} alt="Home Point Inspections" className="h-20 w-auto object-contain rounded-sm" />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.target}
                onClick={() => goTo(l.target)}
                className="font-display uppercase tracking-[0.15em] text-xs text-cream/70 hover:text-gold transition-colors duration-300"
                data-testid={`nav-link-${l.target}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={SPECTORA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center border border-gold/60 text-gold hover:bg-gold hover:text-ink transition-colors duration-300 px-6 py-2.5 font-display uppercase tracking-[0.15em] text-xs"
              data-testid="nav-book-button"
            >
              Book Inspection
            </a>
            <button
              className="md:hidden text-cream"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8 md:hidden"
            data-testid="mobile-menu"
          >
            {LINKS.map((l) => (
              <button
                key={l.target}
                onClick={() => goTo(l.target)}
                className="font-display uppercase tracking-[0.2em] text-2xl text-cream hover:text-gold"
              >
                {l.label}
              </button>
            ))}
            <a
              href={SPECTORA_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="border border-gold text-gold px-8 py-3 font-display uppercase tracking-[0.2em]"
            >
              Book Inspection
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
